/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Pavel Reznak (reznak@cadwork.cz)
 *
 */

import {logInfo} from "App/debug";
import Renderer from "App/renderer";
import Canvas2DContext from "App/canvas_2d_context";
import {vec3} from "gl-matrix";
import WebGLContext from "App/web_gl_context";
import IScene from "js_interface/src/scene_interface";

/**
 * Create abstract render graph
 *
 * Takes care about:
 *  - bins
 *  - passes
 *  - framebuffers
 *
 */
export interface IAbstractRenderGraph {
    getContext(): any;
    release();
    initialize() ;
    removeShape(aShape);
    updateShapeBBox(aShape);
    viewpointChanged();
    notifyMeshCreated(aShape);
    notifySetTextureDataForShapes(aShapes, aImageData);
    notifySetTextureData(aUrl, aImageData);
    scheduleShapesNeedUpdate();
    lightingSettingsChanged();
    scheduleReleaseGLObject(aGLObject);
    addShape(aShape);
    countShapesAndUniqueTextures(aStatistics);
    tick();
    resize(aView);
    render(aView);
    renderDepth(aView, aProperties);
    renderDepthBellowCamera(aView, aProperties);
    renderDepthCustomCamera(aView, aProperties);
    renderGeometryObjectUID(aView, aProperties);
    renderPrimitiveId(aView, aProperties);
}

export class AbstractRenderGraph implements IAbstractRenderGraph{
    protected _sceneAPI: IScene;
    protected _renderer: Renderer;
    protected _context: WebGLContext | Canvas2DContext;

    constructor(aSceneAPI: IScene, aRenderer: Renderer, aContext: Canvas2DContext | WebGLContext) {
        this._sceneAPI = aSceneAPI;
        this._renderer = aRenderer;
        this._context = aContext;
    }

    public getSceneAPI(): IScene {
        return this._sceneAPI;
    }

    public getContext(): WebGLContext | Canvas2DContext {
        return this._context;
    }

    public release() {
    }

    public initialize() {
    }

    public removeShape(aShape) {
        /* jshint unused:vars */
    }

    // UPDATE ---------------------------------------------------------------------

    public updateShapeBBox(aShape) {
        /* jshint unused:vars */
    }

    /**
     * Function is called every time a viewpoint has changed
     */
    public viewpointChanged() {
    }

    public notifyMeshCreated(aShape) {
        /* jshint unused:vars */
    }

    public notifySetTextureDataForShapes(aShapes, aImageData) {
        /* jshint unused:vars */
    }

    public notifySetTextureData(aUrl, aImageData) {
        /* jshint unused:vars */
    }

    public scheduleShapesNeedUpdate() {
    }

    public lightingSettingsChanged() {
    }

    public scheduleReleaseGLObject(aGLObject) {
        /* jshint unused:vars */
    }

    public addShape(aShape) {
        /* jshint unused:vars */
    }

    public countShapesAndUniqueTextures(aStatistics) {
        /* jshint unused:vars */
    }

    public tick() {
    }

    public resize(aView) {
        /* jshint unused:vars */
    }

    public render(aView) {
        /* jshint unused:vars */
    }

    public renderDepth(aView, aProperties): vec3[] {
        /* jshint unused:vars */
        logInfo("Implement renderDepth");
        return [];
    }

    public renderDepthBellowCamera(aView, aProperties) {
        /* jshint unused:vars */
        logInfo("Implement renderDepthBellowCamera");
    }

    public renderDepthCustomCamera(aView, aProperties) {
        /* jshint unused:vars */
        logInfo("Implement renderDepthCustomCamera");
    }

    public renderGeometryObjectUID(aView, aProperties) {
        /* jshint unused:vars */
        logInfo("Implement renderGeometryObjectUID");
    }

    public renderPrimitiveId(aView, aProperties) {
        /* jshint unused:vars */
        logInfo("Implement renderPrimitiveId");
    }
}